import { X } from 'lucide-react';
import { Badge, badgeVariants } from '~/components/ui/badge';
import { cn } from '~/lib/utils';
import { type VariantProps } from 'class-variance-authority';

export type ChipProps = {
  text: React.ReactNode;
  icon?: React.ReactNode;
  level: 'alert' | 'warning' | 'success' | 'info' | 'neutral';
  className?: string;
  role?: React.AriaRole;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onDelete?: React.MouseEventHandler<HTMLDivElement>;
  isDeletable?: boolean;
};

/** @deprecated Prefer Badge `variant`; kept for call sites that read level classes. */
export const colorMap: Record<ChipProps['level'], string> = {
  alert: badgeVariants({ variant: 'alert' }),
  warning: badgeVariants({ variant: 'warning' }),
  success: badgeVariants({ variant: 'success' }),
  info: badgeVariants({ variant: 'info' }),
  neutral: badgeVariants({ variant: 'neutral' }),
};

const levelToVariant: Record<
  ChipProps['level'],
  NonNullable<VariantProps<typeof badgeVariants>['variant']>
> = {
  alert: 'alert',
  warning: 'warning',
  success: 'success',
  info: 'info',
  neutral: 'neutral',
};

export default function Chip(props: ChipProps) {
  return (
    <Badge
      variant={levelToVariant[props.level]}
      className={cn(props.isDeletable && 'relative', props.className)}
      role={props.role}
      onClick={props.onClick}
    >
      {props.icon ? (
        <span
          className="-ml-0.5 inline-flex shrink-0 items-center justify-center text-current [&_svg]:size-3"
          aria-hidden
        >
          {props.icon}
        </span>
      ) : null}
      {props.text}
      {props.isDeletable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            props.onDelete?.(e as unknown as React.MouseEvent<HTMLDivElement>);
          }}
          className="bg-foreground absolute -top-1.5 -right-1.5 cursor-pointer rounded-full p-0.5"
          aria-label="Remove"
        >
          <X className="text-background size-3" strokeWidth={3} />
        </button>
      ) : null}
    </Badge>
  );
}
